import type { Locale } from './index';

export interface FrontPageStrings {
  skip: string;
  lead: string;
  brief: string;
  more: string;
  searchToggle: string;
  langTitle: string;
  langHint: string;
  langClose: string;
  copyLink: string;
  showOriginal: string;
  showTranslation: string;
  loadError: string;
  retry: string;
}

const en: FrontPageStrings = {
  skip: 'Skip to content',
  lead: 'Top story',
  brief: 'Quick news',
  more: 'More stories',
  searchToggle: 'Search',
  langTitle: 'Choose your language',
  langHint: 'Picked from your device. This changes the interface only.',
  langClose: 'Close',
    copyLink: 'Copy link',
    showOriginal: 'Original', showTranslation: 'Translated',
  loadError: 'Could not load the news right now.',
  retry: 'Try again',
};

export const frontPageStrings: Record<Locale, FrontPageStrings> = {
  en,
  vi: {
    skip: 'Bỏ qua tới nội dung', lead: 'Tin chính', brief: 'Điểm tin nhanh', more: 'Các tin khác',
    searchToggle: 'Tìm kiếm', langTitle: 'Chọn ngôn ngữ của bạn',
    langHint: 'Chọn theo thiết bị của bạn. Chỉ đổi ngôn ngữ giao diện.', langClose: 'Đóng',
    copyLink: 'Sao chép liên kết',
    showOriginal: 'Bản gốc', showTranslation: 'Bản dịch',
    loadError: 'Hiện chưa tải được tin.', retry: 'Thử lại',
  },
  zh: {
    skip: '跳到内容', lead: '头条', brief: '快讯', more: '更多新闻', searchToggle: '搜索',
    langTitle: '选择语言', langHint: '根据您的设备选择，仅更改界面语言。', langClose: '关闭',
    copyLink: '复制链接',
    showOriginal: '原文', showTranslation: '译文',
    loadError: '暂时无法加载新闻。', retry: '重试',
  },
  ja: {
    skip: '本文へ移動', lead: 'トップニュース', brief: 'クイックニュース', more: 'その他のニュース', searchToggle: '検索',
    langTitle: '言語を選択', langHint: '端末の設定から選びました。表示言語のみ変わります。', langClose: '閉じる',
    copyLink: 'リンクをコピー',
    showOriginal: '原文', showTranslation: '翻訳',
    loadError: '現在ニュースを読み込めません。', retry: '再試行',
  },
  ko: {
    skip: '본문으로 건너뛰기', lead: '주요 뉴스', brief: '빠른 뉴스', more: '더 많은 뉴스', searchToggle: '검색',
    langTitle: '언어 선택', langHint: '기기 설정에 따라 선택되었습니다. 화면 언어만 바뀝니다.', langClose: '닫기',
    copyLink: '링크 복사',
    showOriginal: '원문', showTranslation: '번역',
    loadError: '지금은 뉴스를 불러올 수 없습니다.', retry: '다시 시도',
  },
  ar: {
    skip: 'انتقل إلى المحتوى', lead: 'الخبر الرئيسي', brief: 'أخبار سريعة', more: 'المزيد من الأخبار', searchToggle: 'بحث',
    langTitle: 'اختر لغتك', langHint: 'تم الاختيار حسب جهازك. يغيّر لغة الواجهة فقط.', langClose: 'إغلاق',
    copyLink: 'نسخ الرابط',
    showOriginal: 'الأصل', showTranslation: 'الترجمة',
    loadError: 'تعذّر تحميل الأخبار الآن.', retry: 'حاول مرة أخرى',
  },
  fr: {
    skip: 'Aller au contenu', lead: 'À la une', brief: 'En bref', more: "Plus d'actualités", searchToggle: 'Rechercher',
    langTitle: 'Choisissez votre langue', langHint: "Choisie d'après votre appareil. Ne change que l'interface.", langClose: 'Fermer',
    copyLink: 'Copier le lien',
    showOriginal: 'Original', showTranslation: 'Traduction',
    loadError: 'Impossible de charger les actualités pour le moment.', retry: 'Réessayer',
  },
  es: {
    skip: 'Saltar al contenido', lead: 'Noticia principal', brief: 'Noticias rápidas', more: 'Más noticias', searchToggle: 'Buscar',
    langTitle: 'Elige tu idioma', langHint: 'Elegido según tu dispositivo. Solo cambia la interfaz.', langClose: 'Cerrar',
    copyLink: 'Copiar enlace',
    showOriginal: 'Original', showTranslation: 'Traducción',
    loadError: 'No se pudieron cargar las noticias ahora.', retry: 'Reintentar',
  },
  ru: {
    skip: 'Перейти к содержимому', lead: 'Главная новость', brief: 'Коротко', more: 'Другие новости', searchToggle: 'Поиск',
    langTitle: 'Выберите язык', langHint: 'Выбран по вашему устройству. Меняется только интерфейс.', langClose: 'Закрыть',
    copyLink: 'Копировать ссылку',
    showOriginal: 'Оригинал', showTranslation: 'Перевод',
    loadError: 'Сейчас не удаётся загрузить новости.', retry: 'Повторить',
  },
  hi: {
    skip: 'सामग्री पर जाएँ', lead: 'मुख्य समाचार', brief: 'त्वरित समाचार', more: 'और समाचार', searchToggle: 'खोजें',
    langTitle: 'अपनी भाषा चुनें', langHint: 'आपके डिवाइस के अनुसार चुनी गई। केवल इंटरफ़ेस की भाषा बदलती है।', langClose: 'बंद करें',
    copyLink: 'लिंक कॉपी करें',
    showOriginal: 'मूल', showTranslation: 'अनुवाद',
    loadError: 'अभी समाचार लोड नहीं हो सके।', retry: 'फिर कोशिश करें',
  },
  id: {
    skip: 'Langsung ke konten', lead: 'Berita utama', brief: 'Berita singkat', more: 'Berita lainnya', searchToggle: 'Cari',
    langTitle: 'Pilih bahasa Anda', langHint: 'Dipilih dari perangkat Anda. Hanya mengubah bahasa antarmuka.', langClose: 'Tutup',
    copyLink: 'Salin tautan',
    showOriginal: 'Asli', showTranslation: 'Terjemahan',
    loadError: 'Berita belum dapat dimuat saat ini.', retry: 'Coba lagi',
  },
  de: {
    skip: 'Zum Inhalt springen', lead: 'Top-Meldung', brief: 'Kurz gemeldet', more: 'Weitere Meldungen', searchToggle: 'Suchen',
    langTitle: 'Sprache wählen', langHint: 'Anhand Ihres Geräts gewählt. Ändert nur die Oberfläche.', langClose: 'Schließen',
    copyLink: 'Link kopieren',
    showOriginal: 'Original', showTranslation: 'Übersetzung',
    loadError: 'Die Nachrichten konnten gerade nicht geladen werden.', retry: 'Erneut versuchen',
  },
  pt: {
    skip: 'Ir para o conteúdo', lead: 'Destaque', brief: 'Notícias rápidas', more: 'Mais notícias', searchToggle: 'Pesquisar',
    langTitle: 'Escolha seu idioma', langHint: 'Escolhido pelo seu dispositivo. Altera apenas a interface.', langClose: 'Fechar',
    copyLink: 'Copiar link',
    showOriginal: 'Original', showTranslation: 'Tradução',
    loadError: 'Não foi possível carregar as notícias agora.', retry: 'Tentar novamente',
  },
  it: {
    skip: 'Vai al contenuto', lead: 'In primo piano', brief: 'Notizie flash', more: 'Altre notizie', searchToggle: 'Cerca',
    langTitle: 'Scegli la tua lingua', langHint: "Scelta in base al dispositivo. Cambia solo l'interfaccia.", langClose: 'Chiudi',
    copyLink: 'Copia link',
    showOriginal: 'Originale', showTranslation: 'Traduzione',
    loadError: 'Impossibile caricare le notizie al momento.', retry: 'Riprova',
  },
};
